import {
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { researchApi } from "../../../services/researchApi";

interface GenerateRequirementsModalProps {
  open: boolean;
  onClose: () => void;
  trackId: string;
  specVersionId?: string;
  onRequirementsGenerated: (runSummary: any, runId?: string) => void;
}

type GenerationState =
  | "idle"
  | "submitting"
  | "polling"
  | "completed"
  | "error";

const POLL_INTERVAL_MS = 3000;

const GenerateRequirementsModal: React.FC<GenerateRequirementsModalProps> = ({
  open,
  onClose,
  trackId,
  specVersionId,
  onRequirementsGenerated,
}) => {
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [generationState, setGenerationState] =
    useState<GenerationState>("idle");
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>("");
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runIdRef = useRef<string | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setGenerationState("idle");
      setErrorMessage("");
      runIdRef.current = null;
    } else {
      stopPolling();
    }
  }, [open, stopPolling]);

  const pollRunStatus = useCallback(
    async (runId: string) => {
      try {
        const run = await researchApi.getRunDetail(runId);

        if (run.status === "completed") {
          stopPolling();
          setGenerationState("completed");
          onRequirementsGenerated(run.run_summary, run.id);
          onClose();
        } else if (run.status === "failed") {
          stopPolling();
          setGenerationState("error");
          const errorInfo = run.run_summary?.error;
          setErrorMessage(
            errorInfo?.message ||
              "Requirements generation failed. Please try again.",
          );
        } else if (run.status === "cancelled") {
          stopPolling();
          setGenerationState("idle");
          onClose();
        }
        // If still running/pending, keep polling
      } catch (err) {
        console.error("Error polling run status:", err);
        // Don't stop polling on transient errors
      }
    },
    [stopPolling, onRequirementsGenerated, onClose],
  );

  const handleGenerate = async () => {
    setGenerationState("submitting");
    setErrorMessage("");

    try {
      const params: {
        additional_instructions?: string;
        spec_version_id?: string;
      } = {};

      if (additionalInstructions.trim()) {
        params.additional_instructions = additionalInstructions.trim();
      }
      if (specVersionId) {
        params.spec_version_id = specVersionId;
      }

      const run = await researchApi.triggerRequirementsGeneration(
        trackId,
        params,
      );
      runIdRef.current = run.id;
      setGenerationState("polling");

      // Start polling
      pollIntervalRef.current = setInterval(() => {
        pollRunStatus(run.id);
      }, POLL_INTERVAL_MS);
    } catch (err: any) {
      setGenerationState("error");
      if (err?.response?.status === 402) {
        setErrorMessage(
          <>
            You don&apos;t have enough credits to run this action. Please
            contact{" "}
            <a href="mailto:support@alpha-affinity.com" style={{ color: "inherit", textDecoration: "underline" }}>
              support@alpha-affinity.com
            </a>{" "}
            to top up your credit balance.
          </>,
        );
      } else {
        setErrorMessage(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Failed to trigger requirements generation.",
        );
      }
    }
  };

  const handleClose = () => {
    if (generationState === "polling" || generationState === "submitting") {
      // Don't allow closing while generating
      return;
    }
    stopPolling();
    onClose();
  };

  const handleCancelRun = async () => {
    const runId = runIdRef.current;
    if (!runId) return;
    stopPolling();
    try {
      await researchApi.cancelRun(runId);
    } catch (err) {
      console.error("Failed to cancel requirements generation:", err);
    }
    setGenerationState("idle");
    onClose();
  };

  const isGenerating =
    generationState === "submitting" || generationState === "polling";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: "#1e1e1e" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#1e1e1e",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6">Generate Requirements</Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={isGenerating} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The AI will generate evaluation criteria based on your project context
          and track type. You can optionally provide additional instructions to
          guide the generation.
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Additional Instructions (optional)"
          placeholder="e.g., Focus on European manufacturers with ISO 9001 certification. Prioritize companies with defense sector experience."
          value={additionalInstructions}
          onChange={(e) => setAdditionalInstructions(e.target.value)}
          disabled={isGenerating}
          variant="outlined"
          sx={{ mb: 1 }}
        />

        {isGenerating && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mt: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: "rgba(33, 150, 243, 0.08)",
              border: 1,
              borderColor: "primary.dark",
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              {generationState === "submitting"
                ? "Starting generation..."
                : "Generating requirements... This may take a moment."}
            </Typography>
          </Box>
        )}

        {generationState === "error" && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: "rgba(244, 67, 54, 0.08)",
              border: 1,
              borderColor: "error.dark",
            }}
          >
            <Typography variant="body2" color="error.light">
              {errorMessage}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ backgroundColor: "#1e1e1e", px: 3, pb: 2 }}>
        {isGenerating ? (
          <Button
            color="error"
            startIcon={<StopIcon />}
            onClick={handleCancelRun}
          >
            Stop Generation
          </Button>
        ) : (
          <Button onClick={handleClose}>Cancel</Button>
        )}
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={isGenerating}
          startIcon={
            isGenerating ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AutoAwesomeIcon />
            )
          }
        >
          {isGenerating ? "Generating..." : "Generate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateRequirementsModal;
